// api
import { parseNumber, parseString, transformModel } from '~/src/dash/api/resource-transforms';
import {
  addDomainAndTokenToUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';

// |------------------ INTERFACES and TYPES ------------------|

export enum ECompanyType {
  TYPE_UNKNOWN = 0,
  TYPE_ADVERTISER,
  TYPE_AGENCY,
  TYPE_AD_NETWORK,
  TYPE_HOUSE_ADVERTISER,
  TYPE_BUY_TYPE,
}

export interface ICompany {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  CompanyType: ECompanyType;
  Name: string;
  PublisherID: number;
  OrderAdvertiserIDs?: number[];
  OrderAgencyIDs?: number[];
}

// |------------------ TRANSFORM FUNCTIONS ------------------|

export const transformCompanyFromAPI = transformModel<ICompany>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  CompanyType: parseNumber(['CompanyType']),
  Name: parseString(['Name']),
  PublisherID: parseNumber(['PublisherID']),
  OrderAdvertiserIDs: ['OrderAdvertiserIDs'],
  OrderAgencyIDs: ['OrderAgencyIDs'],
});

// removed the dates
export const transformCompanyToAPI = transformModel<ICompany>({
  ID: parseNumber(['ID']),

  CompanyType: parseNumber(['CompanyType']),
  Name: parseString(['Name']),
  PublisherID: parseNumber(['PublisherID']),
});

// |------------------ API CALLS ------------------|
export async function getCompanies(publisherID: string): Promise<ICompany[]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/companies`);
  return await restGet<any>(reqUrl, {}).then((val: IHttpResponse<any>) => {
    const companies = [] as ICompany[];
    if (val.parsedBody) {
      for (const item of val.parsedBody) {
        const Company = transformCompanyFromAPI(item);
        companies.push(Company);
      }
    }
    return companies;
  });
}

export async function createCompany(company: ICompany, publisherID: string): Promise<ICompany> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/companies`);
  const parsedCompany = transformCompanyToAPI(company);
  return await restPost<ICompany>(reqUrl, parsedCompany).then((val: IHttpResponse<any>) => {
    return transformCompanyFromAPI(val.parsedBody);
  });
}

// NOTE: Because a full object is required, make sure that all fields in the object are defined in the interface/transform
export async function updateCompany(
  company: ICompany,
  publisherID: string,
  fullUpdate: boolean,
): Promise<ICompany> {
  const partialUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/companies/${company.ID}`);
  const reqUrl = fullUpdate ? partialUrl + '&full_update=true' : partialUrl;
  const parsedCompany = transformCompanyToAPI(company);
  return await restPatch<ICompany>(reqUrl, fullUpdate ? parsedCompany : company).then(
    (val: IHttpResponse<any>) => {
      return transformCompanyFromAPI(val.parsedBody);
    },
  );
}

export async function deleteCompany(
  companyID: string,
  publisherID: string,
): Promise<IHttpResponse<ICompany>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/companies/${companyID}`);
  return await restDelete<ICompany>(reqUrl, undefined);
}
